import { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  MessageCircleQuestion, CheckCircle, Clock, XCircle, Search,
  Send, Trash2, BookOpen, Image as ImageIcon, BookMarked, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

const STATUS_MAP = {
  pending: { label: 'قيد المراجعة', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'معتمد', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  answered: { label: 'تمت الإجابة', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  rejected: { label: 'مرفوض', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const AdminQuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBook, setFilterBook] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [courses, setCourses] = useState([]);
  const [books, setBooks] = useState([]);
  const [answerModal, setAnswerModal] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [answerRefs, setAnswerRefs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [pageModal, setPageModal] = useState(null); // { book_key, page_num, book_title }
  const [draftSaved, setDraftSaved] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    loadQuestions();
    loadCoursesAndBooks();
  }, [filterStatus, filterBook, filterCourse]); // eslint-disable-line

  const loadCoursesAndBooks = async () => {
    try {
      const res = await api.get('/courses');
      setCourses(res.data || []);
      // Fetch books from all courses
      const allBooks = [];
      for (const c of (res.data || []).slice(0, 10)) {
        try {
          const bRes = await api.get(`/courses/${c.course_id}/progress`);
          (bRes.data || []).forEach(b => {
            if (!allBooks.find(x => x.book_key === b.book_key)) {
              allBooks.push({ book_key: b.book_key, title: b.title, course_id: b.course_id });
            }
          });
        } catch {}
      }
      setBooks(allBooks);
    } catch {}
  };

  const loadQuestions = async () => {
    try {
      let url = '/admin/questions?';
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterBook) url += `book_key=${filterBook}&`;
      if (filterCourse) url += `course_id=${filterCourse}&`;
      const res = await api.get(url);
      setQuestions(res.data || []);
    } catch { toast.error('فشل تحميل الأسئلة'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/questions/${id}`, { status });
      toast.success('تم تحديث الحالة');
      loadQuestions();
    } catch { toast.error('فشل التحديث'); }
  };

  const openAnswerModal = (q) => {
    setAnswerModal(q);
    // Try to restore draft from localStorage; otherwise use server data
    let restored = false;
    try {
      const raw = localStorage.getItem(`answer_draft_${q.id}`);
      if (raw) {
        const draft = JSON.parse(raw);
        // Only restore if draft is newer/different than the saved answer
        if (draft.text && draft.text !== q.answer) {
          setAnswerText(draft.text || '');
          setAnswerRefs(Array.isArray(draft.refs) ? draft.refs : []);
          toast.info('تم استرجاع مسودتك المحفوظة تلقائياً');
          restored = true;
        }
      }
    } catch {}
    if (!restored) {
      setAnswerText(q.answer || '');
      setAnswerRefs(q.answer_refs || []);
    }
  };

  const submitAnswer = async () => {
    const plain = (answerText || '').replace(/<[^>]+>/g, '').trim();
    if (!plain) { toast.error('يرجى كتابة الإجابة'); return; }
    setSubmitting(true);
    try {
      await api.put(`/admin/questions/${answerModal.id}`, {
        answer: answerText,
        answer_refs: answerRefs.filter(r => r && r.trim()),
      });
      toast.success('تم إرسال الإجابة وإشعار السائل');
      // Clear draft from localStorage on successful submit
      try { localStorage.removeItem(`answer_draft_${answerModal.id}`); } catch {}
      setAnswerModal(null);
      setAnswerText('');
      setAnswerRefs([]);
      loadQuestions();
    } catch { toast.error('فشل إرسال الإجابة'); }
    finally { setSubmitting(false); }
  };

  // Auto-save draft to localStorage every 2s while editing
  useEffect(() => {
    if (!answerModal) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(`answer_draft_${answerModal.id}`, JSON.stringify({
          text: answerText, refs: answerRefs, savedAt: Date.now(),
        }));
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 1200);
      } catch {}
    }, 1500);
    return () => clearTimeout(t);
  }, [answerText, answerRefs, answerModal]);

  const insertReference = () => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    // Step 1: Insert a placeholder [?] at cursor; Superscript mark wraps it
    editor.chain().focus().insertContent('<sup>[?]</sup>').run();
    try {
      editor.view.dispatch(editor.state.tr.setStoredMarks([]));
    } catch {}

    // Step 2: Read HTML and find ALL sup markers in document order (text-content based)
    const html = editor.getHTML();
    const regex = /<sup>\[([^\]]*)\]<\/sup>/g;
    const matches = [];
    let m;
    while ((m = regex.exec(html)) !== null) {
      matches.push({ full: m[0], content: m[1], index: m.index });
    }
    const newPos = matches.findIndex(x => x.content === '?');
    if (newPos === -1) return;

    // Step 3: Rebuild HTML with sequential numbering in document order
    let newHtml = '';
    let lastEnd = 0;
    matches.forEach((mm, i) => {
      const newNum = i + 1;
      newHtml += html.slice(lastEnd, mm.index);
      newHtml += `<sup>[${newNum}]</sup>`;
      lastEnd = mm.index + mm.full.length;
    });
    newHtml += html.slice(lastEnd);

    // Step 4: Rebuild refs array preserving existing refs at new positions
    const newRefs = matches.map(mm => {
      if (mm.content === '?') return '';
      const oldIdx = parseInt(mm.content, 10) - 1;
      if (isNaN(oldIdx) || oldIdx < 0) return '';
      return answerRefs[oldIdx] || '';
    });

    editor.commands.setContent(newHtml);
    try {
      editor.view.dispatch(editor.state.tr.setStoredMarks([]));
    } catch {}
    setAnswerText(newHtml);
    setAnswerRefs(newRefs);

    setTimeout(() => {
      const el = document.querySelector(`[data-testid="ref-input-${newPos}"]`);
      if (el) el.focus();
    }, 150);
  };

  const updateRef = (i, val) => {
    setAnswerRefs(prev => prev.map((r, idx) => idx === i ? val : r));
  };

  const removeRef = (i) => {
    const removedNum = i + 1;
    let html = answerText || '';
    // Remove the target marker
    html = html.replace(new RegExp(`<sup>\\[${removedNum}\\]</sup>`, 'g'), '');
    // Renumber subsequent markers in document
    const matches = [];
    const re = /<sup>\[([^\]]*)\]<\/sup>/g;
    let m;
    while ((m = re.exec(html)) !== null) matches.push({ full: m[0], index: m.index });
    let newHtml = '';
    let lastEnd = 0;
    matches.forEach((mm, idx) => {
      newHtml += html.slice(lastEnd, mm.index);
      newHtml += `<sup>[${idx + 1}]</sup>`;
      lastEnd = mm.index + mm.full.length;
    });
    newHtml += html.slice(lastEnd);
    setAnswerText(newHtml);
    if (editorRef.current?.editor) {
      editorRef.current.editor.commands.setContent(newHtml);
    }
    setAnswerRefs(prev => prev.filter((_, idx) => idx !== i));
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('هل تريد حذف هذا السؤال؟')) return;
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success('تم حذف السؤال');
      loadQuestions();
    } catch { toast.error('فشل الحذف'); }
  };

  const filtered = questions.filter(q => {
    if (searchQuery) {
      const s = searchQuery.toLowerCase();
      return q.question?.toLowerCase().includes(s) || q.selected_text?.toLowerCase().includes(s) || q.reader_name?.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-6" dir="rtl" data-testid="admin-questions-page">
      <div className="flex items-center gap-3">
        <MessageCircleQuestion className="w-8 h-8 text-amber-700" />
        <h1 className="text-2xl font-bold text-amber-900">إدارة أسئلة المساقات</h1>
        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-sm font-bold">{questions.length}</span>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="بحث في الأسئلة..."
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
              data-testid="search-questions"
            />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
            data-testid="filter-status">
            <option value="">كل الحالات</option>
            <option value="pending">قيد المراجعة</option>
            <option value="approved">معتمد</option>
            <option value="answered">تمت الإجابة</option>
            <option value="rejected">مرفوض</option>
          </select>
          <select value={filterCourse} onChange={e => { setFilterCourse(e.target.value); setFilterBook(''); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
            data-testid="filter-course">
            <option value="">كل التصنيفات</option>
            {courses.map(c => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
          </select>
          <select value={filterBook} onChange={e => setFilterBook(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
            data-testid="filter-book">
            <option value="">كل الكتب</option>
            {books.filter(b => !filterCourse || b.course_id === filterCourse).map(b => (
              <option key={b.book_key} value={b.book_key}>{b.title}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Questions List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">جارٍ التحميل...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageCircleQuestion className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">لا توجد أسئلة</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(q => {
            const statusInfo = STATUS_MAP[q.status] || STATUS_MAP.pending;
            const StatusIcon = statusInfo.icon;
            return (
              <Card key={q.id} className="p-4" data-testid={`question-card-${q.id}`}>
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1 space-y-2">
                    {/* Status badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${statusInfo.color}`}>
                        <StatusIcon className="w-3 h-3" /> {statusInfo.label}
                      </span>
                      {q.book_title && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-900" data-testid={`book-title-${q.id}`}>
                          <BookOpen className="w-3 h-3" /> {q.book_title}
                        </span>
                      )}
                      <button
                        onClick={() => setPageModal({ book_key: q.book_key, page_num: q.page_num, book_title: q.book_title })}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-900 hover:bg-blue-200 transition-colors"
                        data-testid={`view-page-${q.id}`}
                        title="عرض صفحة السؤال"
                      >
                        <ImageIcon className="w-3 h-3" /> صفحة {q.page_num}
                      </button>
                      <span className="text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString('ar-OM')}</span>
                    </div>

                    {/* Selected text */}
                    {q.selected_text && (
                      <div className="bg-amber-50 rounded-lg p-2 border-r-4 border-amber-300">
                        <p className="text-xs text-amber-600 mb-0.5">النص المحدد:</p>
                        <p className="text-sm text-amber-900">{q.selected_text}</p>
                      </div>
                    )}

                    {/* Question */}
                    <div className="bg-blue-50 rounded-lg p-2 border-r-4 border-blue-300">
                      <p className="text-xs text-blue-600 mb-0.5">السؤال:</p>
                      <p className="text-sm text-blue-900 font-medium">{q.question}</p>
                    </div>

                    {/* Answer if exists */}
                    {q.answer && (
                      <div className="bg-emerald-50/40 rounded-lg p-2 border-r-4 border-emerald-200">
                        <p className="text-xs text-emerald-600 mb-0.5">الإجابة:</p>
                        <div className="text-sm text-emerald-900 prose prose-sm max-w-none answer-html"
                          dir="rtl"
                          dangerouslySetInnerHTML={{ __html: q.answer }} />
                        {Array.isArray(q.answer_refs) && q.answer_refs.length > 0 && (
                          <ol className="mt-2 pt-2 border-t border-emerald-200 text-xs text-emerald-800 list-decimal pr-5 space-y-0.5" dir="rtl">
                            {q.answer_refs.map((r, i) => <li key={i}>{r}</li>)}
                          </ol>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex md:flex-col gap-2 shrink-0">
                    {q.status === 'pending' && (
                      <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => updateStatus(q.id, 'approved')} data-testid={`approve-${q.id}`}>
                        <CheckCircle className="w-4 h-4 ml-1" /> اعتماد
                      </Button>
                    )}
                    {(q.status === 'pending' || q.status === 'approved') && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => openAnswerModal(q)}
                        data-testid={`answer-${q.id}`}>
                        <Send className="w-4 h-4 ml-1" /> إجابة
                      </Button>
                    )}
                    {q.status === 'answered' && (
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => openAnswerModal(q)}>
                        <Send className="w-4 h-4 ml-1" /> تعديل الإجابة
                      </Button>
                    )}
                    {q.status === 'pending' && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => updateStatus(q.id, 'rejected')} data-testid={`reject-${q.id}`}>
                        <XCircle className="w-4 h-4 ml-1" /> رفض
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50"
                      onClick={() => deleteQuestion(q.id)} data-testid={`delete-${q.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Answer Modal */}
      <Dialog open={!!answerModal} onOpenChange={(open) => { if (!open) { setAnswerModal(null); setAnswerRefs([]); setAnswerText(''); } }}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-amber-900">كتابة الإجابة</DialogTitle>
          </DialogHeader>
          {answerModal && (
            <div className="space-y-4">
              {answerModal.selected_text && (
                <div className="bg-amber-50 rounded-lg p-3 border-r-4 border-amber-300 text-sm text-amber-900">
                  "{answerModal.selected_text}"
                </div>
              )}
              <div className="bg-blue-50 rounded-lg p-3 border-r-4 border-blue-300">
                <p className="text-sm text-blue-900 font-medium">{answerModal.question}</p>
                <p className="text-xs text-blue-500 mt-1">{answerModal.book_title || ''} — صفحة {answerModal.page_num}</p>
              </div>

              {/* Rich Text Editor with Footnote support */}
              <div data-testid="answer-rich-editor">
                <RichTextEditor
                  value={answerText}
                  onChange={setAnswerText}
                  onEditorReady={(ed) => { editorRef.current = { editor: ed }; }}
                  extraToolbar={(_editor, MenuButton) => (
                    <MenuButton onClick={insertReference} title="إضافة مرجع/مصدر (يضع رقماً ويُنشئ صفاً للمصدر في الأسفل)">
                      <BookMarked size={16} />
                    </MenuButton>
                  )}
                />
              </div>

              {/* Autosave indicator */}
              <div className="flex items-center justify-between text-xs">
                <span className={`transition-opacity duration-300 ${draftSaved ? 'opacity-100' : 'opacity-50'} text-gray-500 flex items-center gap-1`}>
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  {draftSaved ? 'تم الحفظ التلقائي' : 'الحفظ التلقائي مُفعَّل (في متصفحك)'}
                </span>
                <span className="text-gray-400">سيُحفظ كل ثانيتين أثناء الكتابة</span>
              </div>

              {/* References Panel — Word-style: number + row */}
              <div className="border-2 border-amber-200 rounded-xl bg-amber-50/40 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                    <BookMarked className="w-4 h-4" /> المراجع والمصادر ({answerRefs.length})
                  </h4>
                  <Button size="sm" variant="outline" onClick={insertReference} className="text-amber-700 border-amber-300 hover:bg-amber-100" data-testid="add-ref-btn">
                    <Plus className="w-3 h-3 ml-1" /> إضافة مرجع
                  </Button>
                </div>
                {answerRefs.length === 0 ? (
                  <p className="text-xs text-gray-500 py-2 text-center">
                    اضغط "إضافة مرجع" — سيُدرج رقم في موضع المؤشر، ويظهر صف هنا للكتابة.
                  </p>
                ) : (
                  <ol className="space-y-1.5 list-none p-0">
                    {answerRefs.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="shrink-0 w-7 h-7 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center font-bold text-xs">{i + 1}</span>
                        <input
                          value={r}
                          onChange={e => updateRef(i, e.target.value)}
                          placeholder="اكتب المصدر هنا..."
                          className="flex-1 border border-amber-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-amber-400"
                          dir="rtl"
                          data-testid={`ref-input-${i}`}
                        />
                        <button onClick={() => removeRef(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="حذف المرجع" data-testid={`remove-ref-${i}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <Button onClick={submitAnswer} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-answer-btn">
                {submitting ? 'جارٍ الإرسال...' : 'إرسال الإجابة'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Page Image Modal — shows the exact page the question was anchored to */}
      <Dialog open={!!pageModal} onOpenChange={() => setPageModal(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-amber-900">{pageModal?.book_title} — صفحة {pageModal?.page_num}</DialogTitle>
          </DialogHeader>
          {pageModal && (
            <div className="bg-gray-100 rounded-lg p-2 flex justify-center min-h-[300px] items-center">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/books/${pageModal.book_key}/page/${pageModal.page_num}?token=${localStorage.getItem('token') || ''}`}
                alt={`صفحة ${pageModal.page_num}`}
                className="max-w-full h-auto rounded shadow"
                style={{ maxHeight: '75vh' }}
                data-testid="question-page-image"
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.insertAdjacentHTML('beforeend', '<div class="text-red-600 text-sm py-8">تعذّر تحميل الصفحة</div>'); }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestionsPage;
